import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Badge from '@mui/material/Badge';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import SportsSoccerRoundedIcon from '@mui/icons-material/SportsSoccerRounded';
import { useAuth } from '../AuthContext';
import api from '../api';
import tokens from '../theme/tokens';
import useTempoReal from '../utils/tempoReal';

// Altura da barra (sem a área segura do iPhone); o layout reserva esse espaço no celular.
export const BOTTOM_NAV_HEIGHT = 64;

/**
 * Atalhos fixos no rodapé para celular (jogadores logados): Painel, Confirmação, Partidas (pelada aberta
 * com alguém no check-in), Avaliação (só enquanto houver avaliação aberta), Financeiro e Perfil.
 */
const BottomNav = () => {
  const { isAuthenticated, userLogado } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [avaliacaoAberta, setAvaliacaoAberta] = useState(null); // peladaId quando aberta
  const [temPartidas, setTemPartidas] = useState(false); // pelada aberta com check-in

  const jogador = isAuthenticated && userLogado && !['Arbitro', 'Goleiro'].includes(userLogado.perfil);

  useEffect(() => {
    if (!jogador) return;
    api
      .get('avaliacoes/ultima')
      .then((r) => setAvaliacaoAberta(r.data.pelada && r.data.pelada.avaliacao?.status === 'aberta' ? r.data.pelada._id : null))
      .catch(() => setAvaliacaoAberta(null));
  }, [jogador, pathname]);

  const verPartidas = () =>
    api
      .get('getPeladaActive')
      .then((r) => setTemPartidas(Boolean(r.data.data && (r.data.data.arrivedPlayers || []).length > 0)))
      .catch(() => {});

  useEffect(() => {
    if (jogador) verPartidas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jogador, pathname]);

  // Tempo real: o atalho aparece no primeiro check-in (depois disso não precisa mais consultar a cada gravação)
  useTempoReal(
    (canais) => {
      if (!canais || canais.includes('pelada')) verPartidas();
    },
    { ativo: Boolean(jogador && !temPartidas) }
  );

  if (!jogador) return null;

  const itens = [
    { to: '/painel', label: 'Painel', icon: <DashboardRoundedIcon /> },
    { to: '/ordem-chegada', label: 'Confirmação', icon: <PlaceRoundedIcon /> },
    temPartidas && { to: '/partidas', label: 'Partidas', icon: <SportsSoccerRoundedIcon /> },
    avaliacaoAberta && {
      to: `/avaliacao/${avaliacaoAberta}`,
      match: '/avaliacao',
      label: 'Avaliar',
      icon: <Badge color="error" variant="dot"><StarRoundedIcon /></Badge>,
    },
    { to: '/financeiro', label: 'Financeiro', icon: <AccountBalanceWalletRoundedIcon /> },
    { to: '/perfil', label: 'Perfil', icon: <PersonRoundedIcon /> },
  ].filter(Boolean);

  const atual = itens.find((i) => pathname.startsWith(i.match || i.to));

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        display: { xs: 'block', md: 'none' },
        borderRadius: 0,
        borderTop: `1px solid ${tokens.color.border}`,
        pb: 'env(safe-area-inset-bottom)',
      }}
    >
      <BottomNavigation
        showLabels
        value={atual ? atual.to : false}
        onChange={(e, to) => navigate(to)}
        sx={{ height: BOTTOM_NAV_HEIGHT, '& .Mui-selected': { color: tokens.color.brand } }}
      >
        {itens.map((item) => (
          <BottomNavigationAction
            key={item.to}
            value={item.to}
            label={item.label}
            icon={item.icon}
            // Com 6 atalhos (Partidas + Avaliar), o rótulo encolhe para caber em telas de 360 px
            sx={{ minWidth: 0, px: 0.25, '& .MuiBottomNavigationAction-label': itens.length > 5 ? { fontSize: '0.65rem !important' } : undefined }}
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
};

export default BottomNav;
